import { useState, useEffect, useRef } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import {
  Plus, Trash2, Edit, Eye, Printer, X, Search,
  FileText, Truck, Check, Package, ScanLine, Send, Calculator, Loader2
} from 'lucide-react';
import BarcodeScanner from '../../components/BarcodeScanner';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import WhatsAppSendButton from '../../components/WhatsAppSendButton';
import ProjectSelect from '../../components/ProjectSelect';

// Common truck body lengths, offered as suggestions (any value can be typed)
const VEHICLE_LENGTHS = ['14 ft', '17 ft', '19 ft', '20 ft', '22 ft', '24 ft', '32 ft', '40 ft'];
const UNITS = ['pcs', 'sqft', 'rft', 'kg', 'ltr', 'mtr', 'sheet', 'bag', 'box', 'ton', 'set', 'pair'];
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};
const STATUS_LABELS = { draft: 'Draft', sent: 'Sent', delivered: 'Delivered', cancelled: 'Cancelled' };
const TYPE_LABELS = { delivery: 'Delivery Challan' };
const TYPE_COLORS = {
  delivery: 'bg-teal-100 text-teal-700',
};

const emptyItem = { proto_code: '', project_item_id: null, ticked_boxes: [], item_name: '', description: '', quantity: '', unit: 'pcs', rate: '' };
// Ticked packing boxes saved on a line come back as JSON text
const parseTicked = v => {
  if (Array.isArray(v)) return v;
  try { const a = JSON.parse(v || '[]'); return Array.isArray(a) ? a : []; } catch { return []; }
};
// A box printed with label copies (MJ-10-A, MJ-10-B) is one physical box per label.
// Units = the physical boxes of a packing record: [{ box, label }] (label '' = single box)
const unitsOf = boxes => boxes.flatMap(b => (b.labels && b.labels.length > 1
  ? b.labels.map(label => ({ box: b, label })) : [{ box: b, label: '' }]));
// Is this unit ticked? A tick without a label (saved before labels were tickable) covers all labels.
const isTicked = (ticks, u) => ticks.some(t => t.id === u.box.id && (!t.label || !u.label || t.label === u.label));
const unitTick = u => (u.label ? { id: u.box.id, box_number: u.box.box_number, label: u.label } : { id: u.box.id, box_number: u.box.box_number });
// A box (or label) already ticked on ANOTHER challan can't be used again — it is hidden here
// and the server refuses it. used_on = [{ label ('' = whole box), challan_number }].
const usedElsewhere = u => (u.box.used_on || []).some(x => !x.label || !u.label || x.label === u.label);
const freeUnitsOf = boxes => unitsOf(boxes).filter(u => !usedElsewhere(u));
const takenUnitsOf = boxes => unitsOf(boxes).filter(usedElsewhere);
const takenChallans = units => [...new Set(units.flatMap(u => (u.box.used_on || [])
  .filter(x => !x.label || !u.label || x.label === u.label).map(x => x.challan_number)))];
// Row fields that only live in the form (lookup state), never sent to the server
const cleanItem = ({ _matches, _project, _lookedUp, _looking, _scanned, ...rest }) => rest;
const emptyForm = {
  client_name: '', client_phone: '', client_address: '', client_gstin: '',
  challan_type: 'delivery', project_id: '',
  challan_date: new Date().toISOString().slice(0, 10),
  delivery_date: '', status: 'draft', notes: '',
  tax_percent: '', discount_amount: '0',
  cgst: '', sgst: '', igst: '',
  transport_name: '', vehicle_number: '', vehicle_length: '',
  manual_number: '',
  items: [{ ...emptyItem }],
};

export default function SaleChallanPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('sale_challans');
  // Mark Delivered: the challan waiting for confirmation, and the save in progress
  const [deliverTarget, setDeliverTarget] = useState(null);
  const [delivering, setDelivering] = useState(false);
  const canDeliver = c => canEdit && c && (c.status === 'draft' || c.status === 'sent');
  // Existing packing boxes per Proto Code for the open form: { CODE: [boxes] }
  const [boxMap, setBoxMap] = useState({});
  const [boxLookupKey, setBoxLookupKey] = useState('');   // codes|project the map was loaded for
  const [boxPopup, setBoxPopup] = useState(null);         // { i } — index of the challan row
  // Tick / untick one physical box (a label of a multi-label box, or a single box)
  const toggleTick = (i, unit, units) => updateRow(i, row => {
    const cur = parseTicked(row.ticked_boxes);
    // Spell out an old whole-box tick as one tick per label before changing a single label
    const sameBox = units.filter(u => u.box.id === unit.box.id);
    let list = cur.flatMap(t => (t.id === unit.box.id && !t.label && unit.label ? sameBox.map(unitTick) : [t]));
    list = isTicked(list, unit)
      ? list.filter(t => !(t.id === unit.box.id && (!unit.label || t.label === unit.label)))
      : [...list, unitTick(unit)];
    return { ticked_boxes: list };
  });
  const setAllTicks = (i, units, on) => updateRow(i, () => ({ ticked_boxes: on ? units.map(unitTick) : [] }));
  const { formatCurrency, formatDate, settings } = useSettings();
  const [challans, setChallans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [viewChallan, setViewChallan] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [numberingInfo, setNumberingInfo] = useState({});
  const [projectItems, setProjectItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [showSendModal, setShowSendModal] = useState(null); // challan object
  const [clients, setClients] = useState([]);
  const [sendClientId, setSendClientId] = useState('');
  const [sending, setSending] = useState(false);
  const printRef = useRef();

  // Barcode scanner
  const { scannerOpen, openScanner, closeScanner, handleScan, scanning } = useBarcodeScanner({
    onItemFound: (scannedItem) => {
      setForm(f => {
        const hasEmptyOnly = f.items.length === 1 && !f.items[0].item_name.trim();
        const newItems = hasEmptyOnly ? [scannedItem] : [...f.items, scannedItem];
        return { ...f, items: newItems };
      });
    },
  });

  const loadNumberingInfo = () => {
    api.get('/numbering-info').then(r => setNumberingInfo(r.data || {})).catch(() => {});
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        api.get('/sale-challans'),
        api.get('/projects'),
      ]);
      setChallans(cRes.data.filter(c => c.challan_type !== 'sale'));
      setProjects(pRes.data.filter(p => p.status !== 'deleted'));
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load(); loadNumberingInfo();
    api.get('/admin/clients-list').then(r => setClients(r.data)).catch(() => {});
  }, []);

  // ── Box details from existing Packing (read only) ─────────────────────────
  // Re-runs when a Proto Code or the project changes; one request for all rows.
  // Same loose comparison as the server: letters + digits only, no leading "PROTO"
  // (PROTO_86005 = PROTO-86005 = 86005)
  const codeKey = c => String(c || '').toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/^PROTO/, '');
  // Raw codes go to the server (it loosens them the same way); the key only decides when to reload
  const rawCodes = showModal ? [...new Set(form.items.map(i => String(i.proto_code || '').trim()).filter(c => codeKey(c)))] : [];
  const protoCodesKey = [...new Set(rawCodes.map(codeKey))].sort().join(',');
  const lookupKey = `${protoCodesKey}|${form.project_id || ''}|${editId || ''}`;
  useEffect(() => {
    if (!protoCodesKey) { setBoxMap({}); setBoxLookupKey(lookupKey); return; }
    let live = true;
    const t = setTimeout(() => {
      api.get('/packing/boxes-by-proto', { params: { codes: rawCodes.join(','), project_id: form.project_id || undefined, challan_id: editId || undefined } })
        .then(r => { if (live) { setBoxMap(r.data && typeof r.data === 'object' ? r.data : null); setBoxLookupKey(lookupKey); } })
        // Lookup unavailable: show nothing rather than a wrong "No packing box"
        .catch(() => { if (live) { setBoxMap(null); setBoxLookupKey(lookupKey); } });
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [lookupKey]);
  const boxesLoaded = boxLookupKey === lookupKey && boxMap !== null;

  // ── Form helpers ─────────────────────────────────────────────────────────
  const setItem = (i, field, val) => {
    const next = [...form.items];
    next[i] = { ...next[i], [field]: val };
    setForm(f => ({ ...f, items: next }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (i) => { if (form.items.length > 1) setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })); };

  // Auto-fill client from project
  // When challan type changes, clear manual_number
  const handleTypeChange = (newType) => {
    setForm(f => ({ ...f, challan_type: newType, manual_number: '' }));
  };

  const handleProjectChange = async (projectId) => {
    setProjectItems([]);
    if (!projectId) {
      setForm(f => ({ ...f, project_id: '' }));
      return;
    }
    // Autofill client name from project if empty
    const proj = projects.find(p => String(p.id) === String(projectId));
    setForm(f => ({
      ...f,
      project_id: projectId,
      client_name: (!f.client_name && proj?.client_name) ? proj.client_name : f.client_name,
      client_phone: (!f.client_phone && proj?.client_phone) ? proj.client_phone : f.client_phone,
    }));
    // Fetch project items
    setLoadingItems(true);
    try {
      const res = await api.get(`/projects/${projectId}`);
      setProjectItems(res.data.items || []);
    } catch { /* silently ignore */ }
    finally { setLoadingItems(false); }
  };

  // ── Proto Code → item ────────────────────────────────────────────────────
  const updateRow = (i, patch) => setForm(f => {
    const next = [...f.items];
    if (!next[i]) return f;
    next[i] = { ...next[i], ...(typeof patch === 'function' ? patch(next[i]) : patch) };
    return { ...f, items: next };
  });

  const applyMatch = (i, m) => updateRow(i, row => ({
    proto_code: m.proto_code || row.proto_code,
    project_item_id: m.id,
    item_name: m.item_name || row.item_name,
    description: m.description || m.dimensions || row.description || '',
    unit: m.unit || row.unit || 'pcs',
    quantity: row.quantity || String(m.quantity || 1),
    _project: [m.proj_code, m.project_name].filter(Boolean).join(' — '),
    _matches: null,
    _lookedUp: (m.proto_code || row.proto_code || '').trim(),
  }));

  const lookupProto = async (i, raw) => {
    const code = (raw || '').trim();
    const row = form.items[i];
    if (!row) return;
    if (!code) { updateRow(i, { project_item_id: null, _project: '', _matches: null, _lookedUp: '' }); return; }
    if (code === row._lookedUp) return;
    updateRow(i, { _looking: true });
    try {
      const res = await api.get(`/barcodes/lookup?code=${encodeURIComponent(code)}`);
      const matches = res.data || [];
      // Same code can exist in several projects: prefer the challan's project
      const inProject = form.project_id ? matches.filter(m => String(m.project_id) === String(form.project_id)) : [];
      const pick = inProject.length === 1 ? inProject[0] : matches.length === 1 ? matches[0] : null;
      if (pick) applyMatch(i, pick);
      else updateRow(i, { _matches: inProject.length ? inProject : matches, _lookedUp: code, project_item_id: null });
    } catch (err) {
      updateRow(i, { project_item_id: null, _project: '', _matches: null, _lookedUp: code });
      if (err.response?.status === 404) toast(`No project item for Proto Code "${code}". Type the item name, or correct the code.`, { icon: 'ℹ️' });
      else toast.error('Proto Code lookup failed');
    } finally { updateRow(i, { _looking: false }); }
  };

  const applyProjectItems = () => {
    if (!projectItems.length) return;
    const mapped = projectItems.map(pi => ({
      proto_code: pi.proto_code || '',
      project_item_id: pi.id || null,
      _lookedUp: (pi.proto_code || '').trim(),
      item_name: pi.item_name || '',
      description: pi.description || (pi.dimensions ? pi.dimensions : '') || '',
      quantity: String(pi.quantity || 1),
      unit: pi.unit || 'pcs',
    }));
    setForm(f => ({ ...f, items: mapped }));
  };

  const openCreate = () => {
    setEditId(null);
    setProjectItems([]);
    setForm({ ...emptyForm, challan_date: new Date().toISOString().slice(0, 10), items: [{ ...emptyItem }] });
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await api.get(`/sale-challans/${id}`);
      const c = res.data;
      setEditId(id);
      setForm({
        client_name: c.client_name, client_phone: c.client_phone || '',
        client_address: c.client_address || '', client_gstin: c.client_gstin || '',
        challan_type: c.challan_type || 'delivery',
        project_id: c.project_id || '',
        challan_date: c.challan_date?.slice(0, 10) || '',
        delivery_date: c.delivery_date?.slice(0, 10) || '',
        status: c.status, notes: c.notes || '',
        tax_percent: String(c.tax_percent || ''),
        discount_amount: String(c.discount_amount || '0'),
        cgst: String(c.cgst || ''), sgst: String(c.sgst || ''), igst: String(c.igst || ''),
        transport_name: c.transport_name || '',
        vehicle_number: c.vehicle_number || '',
        vehicle_length: c.vehicle_length || '',
        items: c.items.length ? c.items.map(i => ({
          id: i.id, proto_code: i.proto_code || '', project_item_id: i.project_item_id || null,
          ticked_boxes: parseTicked(i.ticked_boxes),
          _lookedUp: (i.proto_code || '').trim(),
          item_name: i.item_name, description: i.description || '',
          quantity: String(i.quantity), unit: i.unit, rate: String(i.rate),
        })) : [{ ...emptyItem }],
      });
      setShowModal(true);
    } catch { toast.error('Failed to load data'); }
  };

  const openView = async (id) => {
    try {
      const res = await api.get(`/sale-challans/${id}`);
      setViewChallan(res.data);
    } catch { toast.error('Failed to load data'); }
  };

  const handleSave = async () => {
    if (!form.client_name.trim()) return toast.error('Client name required');
    const validItems = form.items.filter(i => i.item_name.trim() && i.quantity);
    if (!validItems.length) return toast.error('Please add at least one item');
    setSaving(true);
    try {
      const items = validItems.map(it => {
        const row = cleanItem(it);
        if (boxesLoaded) {
          const live = boxMap[codeKey(it.proto_code)] || [];
          row.ticked_boxes = parseTicked(it.ticked_boxes).filter(t => {
            const b = live.find(x => x.id === t.id);
            return b && (!t.label || (b.labels || []).includes(t.label)) && !usedElsewhere({ box: b, label: t.label || '' });
          });
        }
        return row;
      });
      const payload = { ...form, items };
      if (editId) {
        await api.put(`/sale-challans/${editId}`, payload);
        toast.success('Challan updated successfully ✅');
        setShowModal(false);
        load();
      } else {
        const res = await api.post('/sale-challans', payload);
        // 202 = approval pending
        if (res.status === 202 || res.data?.approval_status === 'PENDING') {
          const typeLabel = form.challan_type === 'sale' ? 'Sale Invoice' : 'Delivery Challan';
          toast.success(`✅ ${typeLabel} request submitted! Admin approval ke baad banega. (ID: #${res.data.approval_id})`);
          setShowModal(false);
        } else {
          toast.success('Sale Challan created successfully ✅');
          setShowModal(false);
          load();
        }
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  // ── Mark Delivered (status only; items and boxes stay as they are) ──────
  const handleMarkDelivered = async () => {
    const c = deliverTarget;
    if (!c) return;
    setDelivering(true);
    try {
      await api.patch(`/sale-challans/${c.id}/status`, { status: 'delivered' });
      toast.success(`${c.challan_number} marked as Delivered`);
      setViewChallan(v => (v && v.id === c.id ? { ...v, status: 'delivered' } : v));
      setDeliverTarget(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not mark as Delivered'); }
    finally { setDelivering(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sale-challans/${deleteTarget.id}`);
      toast.success('Deleted');
      setDeleteTarget(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const sendToClient = async () => {
    if (!sendClientId) return toast.error('Please select a client');
    setSending(true);
    try {
      await api.patch(`/sale-challans/${showSendModal.id}/send-to-client`, { client_id: sendClientId });
      toast.success('Invoice sent to client portal!');
      setShowSendModal(null); setSendClientId('');
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed to send'); }
    finally { setSending(false); }
  };

  // Full printable HTML of the open challan (used by Print and by WhatsApp)
  const buildPrintHtml = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return '';
    return `<html><head><title>${viewChallan?.challan_number || 'Challan'}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Arial', sans-serif; font-size: 12px; color: #1a1a1a; background: white; }
        @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
      </style></head><body>${content}</body></html>`;
  };

  const handlePrint = () => {
    const html = buildPrintHtml();
    if (!html) return;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = challans.filter(c => {
    if (filterStatus && c.status !== filterStatus) return false;
    if (filterType && c.challan_type !== filterType) return false;
    if (search && !c.challan_number.toLowerCase().includes(search.toLowerCase()) &&
      !c.client_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const companyName = settings.company_name || 'MOZI INNOVATORS LLP';
  const companyAddress = settings.company_address || '';

  // ── Determine print colors per type ─────────────────────────────────────
  const typeColor = (type) => type === 'sale' ? '#7c3aed' : '#0f766e';

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-teal-600" /> Sale Challans
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Create and manage delivery challans and sale challans</p>
        </div>
        {canWrite && <button onClick={openCreate} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-xl font-medium text-sm transition-colors shadow-sm">
          <Plus className="w-4 h-4" /> New Challan
        </button>}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by challan number or client..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400">
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400">
          <option value="">All Status</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <FileText className="w-12 h-12 mb-3 opacity-30" />
            <p className="font-medium">No Challans found</p>
            <p className="text-sm mt-1">Click the New Challan button to create one</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Challan No.</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Project</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-semibold text-teal-600 text-sm">{c.challan_number}</span>
                      <div className="text-xs text-gray-400 mt-0.5">{c.item_count} items</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 text-sm">{c.client_name}</div>
                      {c.client_phone && <div className="text-xs text-gray-400">{c.client_phone}</div>}
                      {c.created_by_name && (
                        <div className="text-xs mt-0.5">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-blue-700 font-semibold">
                            ✏️ {c.created_by_name}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TYPE_COLORS[c.challan_type]}`}>
                        {TYPE_LABELS[c.challan_type]}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-sm text-gray-600">{c.project_name || '—'}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-sm text-gray-600">{formatDate(c.challan_date)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[c.status]}`}>
                        {STATUS_LABELS[c.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {canDeliver(c) && <button onClick={() => setDeliverTarget(c)} className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg whitespace-nowrap transition-colors" title="Mark this challan as Delivered"><Truck className="w-3.5 h-3.5" /> Mark Delivered</button>}
                          <button onClick={() => openView(c.id)} className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                          {canEdit && <button onClick={() => openEdit(c.id)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>}
                          <button onClick={() => { setShowSendModal(c); setSendClientId(''); }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Send to Client Portal"><Send className="w-4 h-4" /></button>
                          {canDelete && <button onClick={() => setDeleteTarget(c)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── CREATE / EDIT MODAL ── */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:860,marginTop:16,marginBottom:16}}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-600" />
                {editId ? 'Edit Challan' : 'Create New Challan'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto max-h-[80vh]">
              {/* Challan Type */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Challan Type</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(TYPE_LABELS).map(([v, l]) => (
                    <button key={v}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${form.challan_type === v ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:border-teal-300'}`} onClick={() => handleTypeChange(v)}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Client Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-teal-500" /> Client / Party Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Client Name <span className="text-red-500">*</span></label>
                    <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                      placeholder="e.g. Sharma Furniture House"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                    <input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">GSTIN</label>
                    <input value={form.client_gstin} onChange={e => setForm(f => ({ ...f, client_gstin: e.target.value }))}
                      placeholder="27AABCU9603R1ZX"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                    <input value={form.client_address} onChange={e => setForm(f => ({ ...f, client_address: e.target.value }))}
                      placeholder="Delivery address"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                  </div>
                </div>
              </div>

              {/* Challan Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-teal-500" /> Challan Info
                </h3>
                {/* Manual number input — shown only when mode=manual for this challan type */}
                {!editId && numberingInfo[form.challan_type]?.mode === 'manual' && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <label className="block text-xs font-semibold text-amber-800 mb-1">
                      {form.challan_type === 'sale' ? 'Invoice' : 'Challan'} Number (Manual) <span className="text-red-500">*</span>
                    </label>
                    <input value={form.manual_number} onChange={e => setForm(f => ({ ...f, manual_number: e.target.value }))}
                      placeholder={numberingInfo[form.challan_type]?.last_used ? `Last used: ${numberingInfo[form.challan_type].last_used}` : 'Enter number e.g. DC-2025'}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
                    {numberingInfo[form.challan_type]?.last_used && (
                      <p className="text-xs text-amber-600 mt-1">Last used: <b>{numberingInfo[form.challan_type].last_used}</b></p>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Project (Optional)</label>
                    <ProjectSelect projects={projects} value={form.project_id} onChange={v => handleProjectChange(v)}
              placeholder="— No Project —" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                    {loadingItems && <p className="text-xs text-teal-500 mt-1">Loading project items...</p>}
                    {!loadingItems && projectItems.length > 0 && (
                      <button type="button" onClick={applyProjectItems}
                        className="mt-1.5 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg py-1.5 transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Import {projectItems.length} items from project
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Challan Date <span className="text-red-500">*</span></label>
                    <input type="date" value={form.challan_date} onChange={e => setForm(f => ({ ...f, challan_date: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Date</label>
                    <input type="date" value={form.delivery_date} onChange={e => setForm(f => ({ ...f, delivery_date: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400">
                      {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Transport */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-teal-500" /> Transport Details (Optional)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Transport / Courier Name</label>
                    <input value={form.transport_name} onChange={e => setForm(f => ({ ...f, transport_name: e.target.value }))}
                      placeholder="e.g. DTDC, Transport Wala"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Vehicle / Docket Number</label>
                    <input value={form.vehicle_number} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))}
                      placeholder="e.g. GJ-01-AB-1234"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Vehicle Length</label>
                    <input value={form.vehicle_length} onChange={e => setForm(f => ({ ...f, vehicle_length: e.target.value }))}
                      list="vehicle-lengths" maxLength={50} placeholder="e.g. 17 ft"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                    <datalist id="vehicle-lengths">{VEHICLE_LENGTHS.map(l => <option key={l} value={l} />)}</datalist>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Items / Products</h3>
                  <div className="flex items-center gap-2">
                    <button onClick={openScanner}
                      className="flex items-center gap-1 text-amber-600 hover:bg-amber-50 px-2 py-1 rounded-lg text-xs font-medium transition-colors border border-amber-200">
                      <ScanLine className="w-3.5 h-3.5" /> {scanning ? 'Looking up...' : 'Scan'}
                    </button>
                    <button onClick={addItem} className="flex items-center gap-1 text-teal-600 hover:bg-teal-50 px-2 py-1 rounded-lg text-xs font-medium transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Add Item
                    </button>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">#</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Proto Code</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Item Name</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 hidden sm:table-cell">Description</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Qty</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Unit</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((item, i) => {
                        return (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-xs text-gray-400 align-top pt-3">{i + 1}</td>
                            <td className="px-2 py-2 w-32 align-top">
                              <div className="relative">
                                <input value={item.proto_code || ''}
                                  onChange={e => {
                                    const v = e.target.value;
                                    // A changed code no longer points at the old item until it is looked up again
                                    updateRow(i, row => v.trim() === (row._lookedUp || '') ? { proto_code: v }
                                      : { proto_code: v, project_item_id: null, _project: '', _matches: null, _lookedUp: '' });
                                  }}
                                  onBlur={e => lookupProto(i, e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupProto(i, e.currentTarget.value); } }}
                                  placeholder="Proto code"
                                  className={`w-full border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-teal-400 ${item.project_item_id ? 'border-teal-300 bg-teal-50/40' : 'border-gray-200'}`} />
                                {item._looking && <Loader2 className="w-3 h-3 animate-spin text-teal-500 absolute right-1.5 top-1.5" />}
                              </div>
                              {item.project_item_id && item._project && <div className="text-[10px] text-teal-600 mt-0.5 truncate" title={item._project}>✓ {item._project}</div>}
                              {item._matches?.length > 0 && (
                                <select defaultValue="" onChange={e => { const m = item._matches.find(x => String(x.id) === e.target.value); if (m) applyMatch(i, m); }}
                                  className="mt-1 w-full border border-amber-300 bg-amber-50 rounded px-1 py-0.5 text-[10px]">
                                  <option value="" disabled>{item._matches.length} matches: pick one</option>
                                  {item._matches.map(m => <option key={m.id} value={m.id}>{m.item_name} ({m.proj_code || m.project_name})</option>)}
                                </select>
                              )}
                              {codeKey(item.proto_code) && boxesLoaded && (() => {
                                const boxes = boxMap[codeKey(item.proto_code)] || [];
                                const units = freeUnitsOf(boxes);
                                const taken = takenUnitsOf(boxes);
                                const tickedN = units.filter(u => isTicked(parseTicked(item.ticked_boxes), u)).length;
                                if (!units.length && taken.length) {
                                  return <div className="mt-1 text-[10px] text-gray-500" title={takenChallans(taken).join(', ')}>All {taken.length} box{taken.length === 1 ? '' : 'es'} on other challans</div>;
                                }
                                return units.length ? (
                                  <button type="button" onClick={() => setBoxPopup({ i })}
                                    className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${tickedN
                                      ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                                      : 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100'}`}>
                                    {tickedN ? <Check className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                                    {tickedN ? `${tickedN}/${units.length} ticked` : `${units.length} box${units.length === 1 ? '' : 'es'}`} · details
                                  </button>
                                ) : <div className="mt-1 text-[10px] text-gray-400">No packing box{form.project_id ? ' in this project' : ''}</div>;
                              })()}
                            </td>
                            <td className="px-2 py-2 align-top">
                              <input value={item.item_name} onChange={e => setItem(i, 'item_name', e.target.value)}
                                placeholder="Item name" className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400" />
                            </td>
                            <td className="px-2 py-2 hidden sm:table-cell">
                              <input value={item.description} onChange={e => setItem(i, 'description', e.target.value)}
                                placeholder="Optional" className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400" />
                            </td>
                            <td className="px-2 py-2 w-16">
                              <input type="number" value={item.quantity} onChange={e => setItem(i, 'quantity', e.target.value)}
                                placeholder="0" className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400" />
                            </td>
                            <td className="px-2 py-2 w-20">
                              <select value={item.unit} onChange={e => setItem(i, 'unit', e.target.value)}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400">
                                {UNITS.map(u => <option key={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-2">
                              {form.items.length > 1 && (
                                <button onClick={() => removeItem(i)} className="p-1 text-gray-300 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes / Terms</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Delivery conditions, payment terms, etc."
                  rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none" />
              </div>
            </div>

            {/* Barcode Scanner */}
            {scannerOpen && (
              <BarcodeScanner
                title="Scan Item Barcode"
                onScan={handleScan}
                onClose={closeScanner}
              />
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
                {saving ? 'Saving...' : <><Check className="w-4 h-4" /> {editId ? 'Update' : 'Create Challan'}</>}
              </button>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* ── VIEW / PRINT MODAL ── */}
      {viewChallan && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:720,marginTop:16,marginBottom:16}}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{viewChallan.challan_number}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[viewChallan.challan_type]}`}>
                  {TYPE_LABELS[viewChallan.challan_type]}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {canDeliver(viewChallan) && (
                  <button onClick={() => setDeliverTarget(viewChallan)}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors">
                    <Truck className="w-4 h-4" /> Mark Delivered
                  </button>
                )}
                <button onClick={handlePrint}
                  className="flex items-center gap-2 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors">
                  <Printer className="w-4 h-4" /> Print / Download
                </button>
                <WhatsAppSendButton
                  documentType="SALE_CHALLAN"
                  referenceId={viewChallan.challan_number || viewChallan.id}
                  fileName={`Challan ${viewChallan.challan_number || viewChallan.id}.pdf`}
                  defaultPhone={viewChallan.client_phone}
                  getHtml={async () => buildPrintHtml()}
                />
                <button onClick={() => setViewChallan(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>

            {/* Printable Content */}
            <div ref={printRef} className="p-0">
              <div style={{ maxWidth: '720px', margin: '0 auto', padding: '28px', fontFamily: 'Arial, sans-serif', fontSize: '12px', color: '#1a1a1a' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '14px', borderBottom: `2px solid ${typeColor(viewChallan.challan_type)}` }}>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: typeColor(viewChallan.challan_type) }}>{companyName}</div>
                    {companyAddress && <div style={{ fontSize: '11px', color: '#555', marginTop: '3px' }}>{companyAddress}</div>}
                    {settings.company_gstin && <div style={{ fontSize: '11px', color: '#555' }}>GSTIN: {settings.company_gstin}</div>}
                    {settings.company_phone && <div style={{ fontSize: '11px', color: '#555' }}>📞 {settings.company_phone}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: typeColor(viewChallan.challan_type), letterSpacing: '1px' }}>
                      {TYPE_LABELS[viewChallan.challan_type].toUpperCase()}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginTop: '2px' }}>{viewChallan.challan_number}</div>
                    <div style={{ marginTop: '4px', display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 600, background: '#f0fdf4', color: '#15803d', textTransform: 'uppercase' }}>
                        {STATUS_LABELS[viewChallan.status]}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Info Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '18px' }}>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px 14px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '6px', letterSpacing: '0.5px' }}>Bill To / Party</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{viewChallan.client_name}</div>
                    {viewChallan.client_phone && <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>📞 {viewChallan.client_phone}</div>}
                    {viewChallan.client_gstin && <div style={{ fontSize: '11px', color: '#475569' }}>GSTIN: {viewChallan.client_gstin}</div>}
                    {viewChallan.client_address && <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>{viewChallan.client_address}</div>}
                  </div>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px 14px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '6px', letterSpacing: '0.5px' }}>Challan Details</div>
                    <div style={{ fontSize: '12px', color: '#1e293b', marginBottom: '3px' }}><b>Date:</b> {formatDate(viewChallan.challan_date)}</div>
                    {viewChallan.delivery_date && <div style={{ fontSize: '12px', color: '#1e293b', marginBottom: '3px' }}><b>Delivery:</b> {formatDate(viewChallan.delivery_date)}</div>}
                    {viewChallan.project_name && <div style={{ fontSize: '12px', color: '#1e293b', marginBottom: '3px' }}><b>Project:</b> {viewChallan.project_name}</div>}
                  </div>
                </div>

                {/* Transport Details - always printed (blank lines when not filled) */}
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '18px', overflow: 'hidden' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#fff', background: typeColor(viewChallan.challan_type), padding: '6px 14px', letterSpacing: '0.5px' }}>Transport Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 0.8fr' }}>
                    {[['Transport / Courier', viewChallan.transport_name], ['Vehicle Number', viewChallan.vehicle_number], ['Vehicle Length', viewChallan.vehicle_length]].map(([k, v], i) => (
                      <div key={k} style={{ padding: '9px 14px', borderLeft: i ? '1px solid #e2e8f0' : 'none' }}>
                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{k}</div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginTop: '3px', minHeight: '16px' }}>{v || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Items Table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
                  <thead>
                    <tr style={{ background: typeColor(viewChallan.challan_type), color: 'white' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 600 }}>#</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 600 }}>Description of Goods</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 600 }}>Details</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '11px', fontWeight: 600 }}>Qty</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '11px', fontWeight: 600 }}>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewChallan.items.map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                        <td style={{ padding: '7px 10px', fontSize: '12px', color: '#64748b' }}>{i + 1}</td>
                        <td style={{ padding: '7px 10px', fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>
                          {it.item_name}
                          {it.proto_code && <div style={{ fontSize: '10px', fontWeight: 400, color: '#64748b', fontFamily: 'monospace' }}>{it.proto_code}</div>}
                          {parseTicked(it.ticked_boxes).length > 0 && <div style={{ fontSize: '10px', fontWeight: 400, color: '#15803d' }}>Boxes: {parseTicked(it.ticked_boxes).map(t => t.label || t.box_number).join(', ')}</div>}
                        </td>
                        <td style={{ padding: '7px 10px', fontSize: '11px', color: '#64748b' }}>{it.description || '—'}</td>
                        <td style={{ padding: '7px 10px', fontSize: '12px', textAlign: 'center' }}>{it.quantity}</td>
                        <td style={{ padding: '7px 10px', fontSize: '12px', textAlign: 'center', color: '#64748b' }}>{it.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Total quantity (a delivery challan carries no prices) */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '18px', fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>
                  Total Qty: {viewChallan.items.reduce((a, it) => a + (parseFloat(it.quantity) || 0), 0)}
                </div>

                {viewChallan.notes && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '10px 14px', marginBottom: '18px', fontSize: '11px', color: '#166534' }}>
                    <b>Notes:</b> {viewChallan.notes}
                  </div>
                )}

                {/* Signatures */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '30px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                  {['Prepared By', 'Checked By', "Receiver's Signature"].map(label => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ borderTop: '1px solid #94a3b8', marginTop: '40px', paddingTop: '6px', fontSize: '10px', color: '#64748b' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* Box details for one challan row (from existing Packing) — tick the boxes for this item */}
      {boxPopup && form.items[boxPopup.i] && (() => {
        const row = form.items[boxPopup.i];
        const code = String(row.proto_code || '').trim();
        const boxes = boxMap[codeKey(code)] || [];
        const ticks = parseTicked(row.ticked_boxes);
        const units = freeUnitsOf(boxes);
        const taken = takenUnitsOf(boxes);
        const tickedN = units.filter(u => isTicked(ticks, u)).length;
        return (
        <div className="modal-backdrop" onClick={() => setBoxPopup(null)}>
          <div className="modal-scroll">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-gray-900 flex items-center gap-2"><Package className="w-4 h-4 text-purple-600" /> Box Details</h3>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    <span className="font-mono font-semibold text-gray-700">{code}</span>{row.item_name ? ` · ${row.item_name}` : ''}
                    {' · '}{units.length} box{units.length === 1 ? '' : 'es'}
                  </p>
                </div>
                <button onClick={() => setBoxPopup(null)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
              {taken.length > 0 && (
                <div className="px-5 py-2 border-b border-amber-100 bg-amber-50 text-xs text-amber-800">
                  {taken.length} box{taken.length === 1 ? ' is' : 'es are'} already on other challans ({takenChallans(taken).join(', ')}) and can't be used again.
                </div>
              )}
              {units.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-5 py-2 border-b border-gray-100 bg-gray-50 text-xs">
                  <span className={tickedN ? 'text-green-700 font-semibold' : 'text-gray-500'}>{tickedN} of {units.length} ticked for this item</span>
                  <span className="flex gap-2">
                    <button type="button" onClick={() => setAllTicks(boxPopup.i, units, true)} className="px-2 py-1 rounded-lg border border-green-300 text-green-700 font-semibold hover:bg-green-50">Tick all</button>
                    {tickedN > 0 && <button type="button" onClick={() => setAllTicks(boxPopup.i, units, false)} className="px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-white">Clear</button>}
                  </span>
                </div>
              )}
              <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
                {units.length === 0 && <p className="text-sm text-gray-400 text-center py-4">{taken.length ? 'No free boxes left for this item' : 'No packing box for this Proto Code'}</p>}
                {units.map(u => {
                  const b = u.box;
                  const on = isTicked(ticks, u);
                  return (
                  <div key={`${b.id}|${u.label}`} className={`border rounded-xl p-3 transition-colors ${on ? 'border-green-400 bg-green-50' : 'border-gray-200'}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" role="checkbox" aria-checked={on} onClick={() => toggleTick(boxPopup.i, u, units)}
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${on ? 'bg-green-600 border-green-600 text-white' : 'bg-white border-gray-300 hover:border-green-500'}`}
                        title={on ? 'Untick' : 'Tick this box'}>
                        {on && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                      </button>
                      <span className={`font-bold ${on ? 'text-green-800' : 'text-gray-900'}`}>{u.label || b.box_number}{b.sub_label ? ` (${b.sub_label})` : ''}</span>
                      {u.label && <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{b.labels.indexOf(u.label) + 1} of {b.labels.length} · {b.box_number}</span>}
                      {b.sale_challan_number && <span className="text-[11px] text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">🧾 {b.sale_challan_number}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-1">
                      {(b.proj_code || b.project_name) && <span>📁 {[b.proj_code, b.project_name].filter(Boolean).join(' — ')}</span>}
                      {b.main_item && <span>📦 {b.main_item}</span>}
                      {b.created_by_name && <span>👤 {b.created_by_name}</span>}
                      {b.created_at && <span>🕐 {formatDate(b.created_at)}</span>}
                      {b.photo_count > 0 && <span>📷 {b.photo_count}</span>}
                    </div>
                    {b.description && <p className="text-xs text-gray-600 mt-1">{b.description}</p>}
                    {b.items.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {b.items.map((l, k) => (
                          <div key={k} className={`flex justify-between gap-2 text-xs rounded-lg px-2.5 py-1.5 ${on ? 'bg-white/70' : 'bg-gray-50'}`}>
                            <span className="text-gray-700">{k + 1}. {l.item_name}</span>
                            <span className="font-bold text-gray-600 whitespace-nowrap">{Number(l.quantity)} {l.unit}</span>
                          </div>
                        ))}
                        <div className="text-right text-[11px] text-gray-500">Total in box: <b className="text-gray-700">{b.total_qty}</b></div>
                      </div>
                    ) : <p className="text-xs text-gray-400 mt-2">No contents listed on this box</p>}
                    {b.notes && <p className="text-[11px] text-gray-500 mt-1 bg-yellow-50 border border-yellow-100 rounded px-2 py-1">📝 {b.notes}</p>}
                  </div>
                  );
                })}
              </div>
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3">
                <span className="text-[11px] text-gray-400">Ticks are saved when you save the challan.</span>
                <button type="button" onClick={() => setBoxPopup(null)} className="px-4 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold">Done</button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}


      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Challan?</h3>
            <p className="text-sm text-gray-600 mb-5"><b>{deleteTarget.challan_number}</b> will be permanently deleted.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium">Delete</button>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* Mark Delivered Confirm */}
      {deliverTarget && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Mark as Delivered?</h3>
            <p className="text-sm text-gray-600 mb-5"><b>{deliverTarget.challan_number}</b> will be marked as Delivered. Its ticked boxes will show as Delivered in Packing.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeliverTarget(null)} disabled={delivering} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleMarkDelivered} disabled={delivering} className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-60">
                {delivering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Mark Delivered
              </button>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* Send to Client Portal Modal */}
      {showSendModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Send to Client Portal</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{showSendModal.challan_number}</p>
                </div>
                <button onClick={()=>setShowSendModal(null)} className="text-gray-400 hover:text-gray-600"><X size={16}/></button>
              </div>
              <p className="text-sm text-gray-600 mb-4">Select the client who should receive this invoice in their portal:</p>
              <select value={sendClientId} onChange={e=>setSendClientId(e.target.value)} className="field-input w-full mb-4">
                <option value="">— Select Client —</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div className="flex gap-3 justify-end">
                <button onClick={()=>setShowSendModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={sendToClient} disabled={sending||!sendClientId}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-60 flex items-center gap-2">
                  <Send size={13}/> {sending?'Sending...':'Send to Client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
